import { useState, useMemo } from 'react';
import { useMapStore } from '../../store/mapStore';

// Signature group type colors and icons
const groupConfig: Record<string, { color: string; bgColor: string; icon: string }> = {
  Wormhole: { color: 'text-purple-400', bgColor: 'bg-purple-900/30', icon: '◎' },
  'Data Site': { color: 'text-cyan-400', bgColor: 'bg-cyan-900/30', icon: '⬡' },
  'Relic Site': { color: 'text-yellow-400', bgColor: 'bg-yellow-900/30', icon: '⬢' },
  'Gas Site': { color: 'text-orange-400', bgColor: 'bg-orange-900/30', icon: '◈' },
  'Combat Site': { color: 'text-red-400', bgColor: 'bg-red-900/30', icon: '⚔' },
  'Ore Site': { color: 'text-gray-400', bgColor: 'bg-gray-800/30', icon: '◇' },
  'Cosmic Signature': { color: 'text-blue-400', bgColor: 'bg-blue-900/30', icon: '◉' },
  'Cosmic Anomaly': { color: 'text-green-400', bgColor: 'bg-green-900/30', icon: '◎' },
  Unknown: { color: 'text-gray-500', bgColor: 'bg-gray-800/20', icon: '?' },
};

// Format relative timestamp (like "1 01:31:15" for 1 day, 1 hour, 31 min, 15 sec ago)
function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    const remainingMinutes = minutes % 60;
    const remainingSeconds = seconds % 60;
    return `${days} ${String(remainingHours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  }

  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    const remainingSeconds = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  }

  if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  }

  return `${seconds}s`;
}

interface SignatureTableProps {
  systemId: string;
}

export default function SignatureTable({ systemId }: SignatureTableProps) {
  const { signatures, updateSignature, removeSignature } = useMapStore();
  const [sortColumn, setSortColumn] = useState<string>('sigId');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  // Filter signatures for this system
  const systemSignatures = useMemo(() => {
    return signatures.filter((sig) => sig.systemId === systemId);
  }, [signatures, systemId]);

  // Sort signatures
  const sortedSignatures = useMemo(() => {
    return [...systemSignatures].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';

      switch (sortColumn) {
        case 'sigId':
          aVal = a.sigId;
          bVal = b.sigId;
          break;
        case 'groupType':
          aVal = a.groupType;
          bVal = b.groupType;
          break;
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'createdAt':
          aVal = a.createdAt;
          bVal = b.createdAt;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
  }, [systemSignatures, sortColumn, sortDirection]);

  // Handle column header click for sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort indicator
  const getSortIndicator = (column: string) => {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  // Start editing a signature name
  const handleStartEdit = (sig: { id: string; name: string | null }) => {
    setEditingId(sig.id);
    setEditName(sig.name || '');
  };

  // Save edited name
  const handleSaveEdit = async () => {
    if (editingId) {
      await updateSignature(editingId, { name: editName || undefined });
      setEditingId(null);
      setEditName('');
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  // Handle key press in edit input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Delete signature
  const handleDelete = async (sigId: string) => {
    await removeSignature(sigId);
  };

  if (systemSignatures.length === 0) {
    return (
      <div className="rounded border border-gray-700 bg-gray-800/50 p-4 text-center">
        <p className="text-sm text-gray-400">No signatures scanned in this system.</p>
        <p className="mt-1 text-xs text-gray-500">
          Click "Import" above to paste signatures from EVE.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded border border-gray-700 bg-gray-800/50 overflow-hidden">
      {/* Signature table */}
      <div>
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-gray-800 border-b border-gray-700">
            <tr className="text-gray-400 text-left">
              <th
                className="px-2 py-2 cursor-pointer hover:text-white transition"
                onClick={() => handleSort('sigId')}
              >
                Id{getSortIndicator('sigId')}
              </th>
              <th
                className="px-2 py-2 cursor-pointer hover:text-white transition"
                onClick={() => handleSort('groupType')}
              >
                Group{getSortIndicator('groupType')}
              </th>
              <th
                className="px-2 py-2 cursor-pointer hover:text-white transition"
                onClick={() => handleSort('name')}
              >
                Info{getSortIndicator('name')}
              </th>
              <th
                className="px-2 py-2 cursor-pointer hover:text-white transition"
                onClick={() => handleSort('createdAt')}
              >
                Added{getSortIndicator('createdAt')}
              </th>
              <th className="px-2 py-2 w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {sortedSignatures.map((sig) => {
              const config = groupConfig[sig.groupType] || groupConfig.Unknown;
              return (
                <tr
                  key={sig.id}
                  className={`hover:bg-gray-700/50 ${config.bgColor}`}
                >
                  {/* Signature ID with icon */}
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className={`${config.color}`}>{config.icon}</span>
                      <span className="font-mono text-white">{sig.sigId}</span>
                    </div>
                  </td>

                  {/* Group type */}
                  <td className={`px-2 py-1.5 ${config.color}`}>
                    {sig.groupType}
                  </td>

                  {/* Info/Name - editable */}
                  <td className="px-2 py-1.5">
                    {editingId === sig.id ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={handleKeyPress}
                        onBlur={handleSaveEdit}
                        autoFocus
                        className="w-full rounded bg-gray-600 px-1.5 py-0.5 text-white text-xs"
                      />
                    ) : (
                      <span
                        onClick={() => handleStartEdit(sig)}
                        className="cursor-pointer text-gray-300 hover:text-white block truncate max-w-[150px]"
                        title={sig.name || 'Click to edit'}
                      >
                        {sig.name || (
                          <span className="text-gray-500 italic">Unknown</span>
                        )}
                      </span>
                    )}
                  </td>

                  {/* Added timestamp */}
                  <td className="px-2 py-1.5 text-gray-400 whitespace-nowrap">
                    {formatRelativeTime(sig.createdAt)}
                  </td>

                  {/* Actions */}
                  <td className="px-2 py-1.5">
                    <button
                      onClick={() => handleDelete(sig.id)}
                      className="text-gray-500 hover:text-red-400 transition"
                      title="Delete signature"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
